/* eslint-disable ember/no-get, no-prototype-builtins */
import EmberObject, { get } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { getFieldOptions } from '@linc-technologies/linced-common/utils/fields';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { stripDiacritics } from 'ember-power-select/utils/group-utils';
import {
  conformDate,
  isTruthy,
} from '@linc-technologies/linced-common/utils/strings';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export const JurisdictionalIdValidations = Object.freeze({
  [Jurisdiction.NZ_MOE]: (val) => /^\d{9,10}$/.test(val),
});

export function conformJurisdictionalId(jurisdiction, id) {
  let validator = JurisdictionalIdValidations[jurisdiction];
  if (!isEmpty(validator) && !validator(id)) {
    // We have a validator, and it says that this ID is invalid.
    return null;
  }

  switch (jurisdiction) {
    case Jurisdiction.NZ_MOE:
      // NSNs should be conformed to 10 digits to enable comparison.
      while (id.length < 10) {
        id = `0${id}`;
      }
      return id;
    default:
      return id;
  }
}

export const RequiredFieldsMap = EmberObject.create({
  // Note: Homeroom is also required, but isn't a dynamic field so is not included here.
  // It is expected to be required for all jurisdictions.
  [Jurisdiction.NZ_MOE]: [
    {
      key: 'core:dob',
      error: 'Please check the Date of Birth',
    },
    {
      key: 'nzMOE:firstSchoolingDate',
      error: 'Please check the First Schooling date',
    },
    {
      key: 'nzMOE:gender',
      error: 'Gender is required',
    },
    {
      key: 'nzMOE:fte',
      error: 'FTE is required',
    },
    {
      key: 'nzMOE:type',
      error: 'Student Type is required',
    },
    {
      key: 'nzMOE:eligibilityCriteria',
      error: 'Eligibility Criteria is required',
    },
    {
      key: 'nzMOE:currentYearLevel',
      error: 'Student must have a current year level',
    },
  ],
  [Jurisdiction.INDEPENDENT_SCHOOL]: [],
});

export function getRequiredFieldsForJurisdiction(jurisdiction) {
  if (RequiredFieldsMap.hasOwnProperty(jurisdiction)) {
    return RequiredFieldsMap[jurisdiction].slice();
  }
  return [];
}

// Labels the 2027 NZ MoE code lists renamed. Imports still accept the
// previous label (without diacritics, as matched below) for the same code.
const PREVIOUS_LABELS = new Map([
  ['Nauru', 'NRU'],
  ['Taranaki Trades Academy (Te Pukenga - WITT)', '15'],
  ['Wellington Trades Academy (Te Pukenga - WelTec)', '19'],
  ['Chinese', 'CHIN'],
  ['Cook Island Maori', 'COOK'],
  ['English as a second language', 'ENSL'],
  ['Niuean', 'NIUE'],
  ['Samoan', 'SAMO'],
  ['Tokelauan', 'TOKE'],
  ['Tongan', 'TONG'],
  ['Vanuatu (Other languages)', 'VANU'],
  ['Technology', 'TECN'],
  ['Environmental Sustainability', 'SENV'],
  ['Commerce related', 'BUSS'],
  ['Legal/Law related studies', 'LAWS'],
  ['Religious education/studies', 'REST'],
  ['Travel, Hospitality, Tourism', 'THTR'],
]);

function findOptionValue(value, options) {
  let query = stripDiacritics(value.trim());
  if (isEmpty(query)) {
    return;
  }
  // Search all values first, then labels.
  let option =
    options.find((option) => {
      return stripDiacritics(option.value) === query;
    }) ||
    options.find((option) => {
      return stripDiacritics(option.label) === query;
    }) ||
    options.find((option) => {
      return option.value === PREVIOUS_LABELS.get(query);
    });

  if (!isEmpty(option)) {
    return option.value;
  }
}
function findNumericOptionValue(value, options) {
  let query = stripDiacritics(value);
  if (isEmpty(query)) {
    return;
  }
  // Search all values first, then labels.
  let option =
    options.find((option) => {
      return option.valueInt === query;
    }) ||
    options.find((option) => {
      return stripDiacritics(option.label) === query;
    });

  if (!isEmpty(option)) {
    return option.valueInt;
  }
}

function clampFieldValue(field, value) {
  let min = get(field, 'htmlAttributes.min') ?? false;
  let max = get(field, 'htmlAttributes.max') ?? false;

  if (min !== false) {
    value = Math.max(min, value);
  }
  if (max !== false) {
    value = Math.min(max, value);
  }

  return value;
}

// MoE retired these iwi responses; import them as Not Stated (9999), as the
// roll return does, rather than dropping them.
const RETIRED_IWI = Object.freeze([
  '4444',
  '5555',
  '7777',
  '8888',
  "don't know",
  'refused to answer',
  'response unidentifiable',
  'response outside scope',
]);

export function conformFieldValue(field, value) {
  let options, rawValues, defaultValue;

  if (field.dataType === DataType.BOOLEAN) {
    return isTruthy(value);
  }

  switch (field.inputType) {
    case InputType.DATE:
      value = conformDate(value);
      break;

    case InputType.NUMBER:
      if (field.dataType === DataType.INTEGER) {
        value = parseInt(value, 10);
        defaultValue = get(field, 'defaultValue.valueInt') ?? 0;
      } else {
        value = parseFloat(value);
        defaultValue = get(field, 'defaultValue.valueFloat') ?? 0.0;
      }

      if (Number.isNaN(value)) {
        value = defaultValue;
      }

      value = clampFieldValue(field, value);
      break;

    case InputType.RADIO:
    case InputType.SELECT:
      options = field.optionsFromEnum
        ? getFieldOptions(field.key)
        : field.options;
      if (field.dataType === DataType.INTEGER) {
        value = findNumericOptionValue(value, options);
      } else {
        value = findOptionValue(value, options);
      }
      break;

    case InputType.CHECKBOX:
    case InputType.MULTI_SELECT:
      options = field.optionsFromEnum
        ? getFieldOptions(field.key)
        : field.options;
      rawValues = value.split(',');
      value = [];
      rawValues.forEach((val) => {
        if (field.key === 'nzMOE:iwi') {
          val = val.trim();
          if (/^\d{1,3}$/.test(val)) {
            // short iwi codes require leading zeros.
            val = val.padStart(4, '0');
          }
          if (RETIRED_IWI.includes(val.toLowerCase())) {
            val = '9999';
          }
        }
        let option = findOptionValue(val, options);
        if (!isEmpty(option) && !value.includes(option)) {
          value.push(option);
        }
      });
      break;
  }

  return value;
}

export function canHashContact(firstName, lastName, phoneHome, phoneMobile) {
  let hasName = !isEmpty(firstName) || !isEmpty(lastName);
  let hasPhone = !isEmpty(phoneMobile) || !isEmpty(phoneHome);
  return hasName && hasPhone;
}

export function flattenList(values, field) {
  if (isEmpty(field)) {
    return;
  }

  // Get options from field or enum as appropriate
  let options = field.options;
  if (field.optionsFromEnum) {
    options = getFieldOptions(field.key);
  }

  if (!isEmpty(field) && !isEmpty(options)) {
    let option;
    values = values.map((value) => {
      if (field.dataType === DataType.INTEGER) {
        option = findBy(options, 'valueInt', value);
      } else {
        option = findBy(options, 'value', value);
      }
      if (isEmpty(option) || isEmpty(option.label)) {
        return value;
      }
      return option.label;
    });
  }

  return [...values].join(', ');
}
